import React from 'react';
import { Layout, Menu, Button, Drawer, Grid } from 'antd';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/authSlice';
import {
  DashboardOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  LogoutOutlined,
  WalletOutlined,
  AppstoreAddOutlined,
  QuestionCircleOutlined,
  MenuOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import logo from '../assets/logo.jpeg';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const screens = Grid.useBreakpoint();
  const [drawerVisible, setDrawerVisible] = React.useState(false);

  // Check if it's mobile view (xs or sm, and not md/lg/xl/xxl)
  // useBreakpoint returns an object like { xs: true, sm: true, md: false, ... }
  // We'll consider it mobile if 'md' is false.
  const isMobile = !screens.md;

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const adminItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/admin/dashboard" onClick={() => setDrawerVisible(false)}>Dashboard</Link>,
    },
    {
      key: '/admin/payouts',
      icon: <WalletOutlined />,
      label: <Link to="/admin/payouts" onClick={() => setDrawerVisible(false)}>Payouts</Link>,
    },
    {
      key: '/admin/products',
      icon: <AppstoreAddOutlined />,
      label: <Link to="/admin/products" onClick={() => setDrawerVisible(false)}>Products</Link>,
    },
    {
      key: '/admin/qr-codes',
      icon: <QrcodeOutlined />,
      label: <Link to="/admin/qr-codes" onClick={() => setDrawerVisible(false)}>QR Codes</Link>,
    },
    {
      key: '/admin/bde-users',
      icon: <UserOutlined />,
      label: <Link to="/admin/bde-users" onClick={() => setDrawerVisible(false)}>BDE Users</Link>,
    },
    {
      key: '/admin/shops',
      icon: <ShopOutlined />,
      label: <Link to="/admin/shops" onClick={() => setDrawerVisible(false)}>Shops</Link>,
    },
    {
      key: '/admin/orders',
      icon: <ShoppingCartOutlined />,
      label: <Link to="/admin/orders" onClick={() => setDrawerVisible(false)}>All Orders</Link>,
    },
  ];

  const bdeItems = [
    {
      key: '/bde/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/bde/dashboard" onClick={() => setDrawerVisible(false)}>Dashboard</Link>,
    },
    {
      key: '/bde/shops',
      icon: <ShopOutlined />,
      label: <Link to="/bde/shops" onClick={() => setDrawerVisible(false)}>My Shops</Link>,
    },
    {
      key: '/bde/orders',
      icon: <ShoppingCartOutlined />,
      label: <Link to="/bde/orders" onClick={() => setDrawerVisible(false)}>My Orders</Link>,
    },
    {
      key: '/bde/payouts',
      icon: <WalletOutlined />,
      label: <Link to="/bde/payouts" onClick={() => setDrawerVisible(false)}>Payout History</Link>,
    },
    {
      key: '/bde/profile',
      icon: <UserOutlined />,
      label: <Link to="/bde/profile" onClick={() => setDrawerVisible(false)}>My Profile</Link>,
    },
    {
      key: '/bde/help',
      icon: <QuestionCircleOutlined />,
      label: <Link to="/bde/help" onClick={() => setDrawerVisible(false)}>Help</Link>,
    },
  ];

  const menuItems = user?.role === 'super_admin' ? adminItems : bdeItems;

  const SidebarContent = (
    <>
      <div className="sidebar-logo" style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.02)',
        padding: '8px'
      }}>
        <img src={logo} alt="Admin Logo" style={{ maxHeight: '100%', maxWidth: '80%', objectFit: 'contain' }} />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        defaultSelectedKeys={[location.pathname]}
        items={menuItems}
        style={{
          background: 'transparent',
          borderRight: 0,
          padding: '16px 8px'
        }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: 0, background: '#0f172a' }}
          width={260}
          closable={false}
        >
          {SidebarContent}
        </Drawer>
      ) : (
        /* Desktop Sidebar */
        <Sider
          collapsible
          width={260}
          style={{
            background: '#0f172a',
            boxShadow: '4px 0 24px 0 rgba(0,0,0,0.02)',
            zIndex: 1001,
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
          }}
          trigger={null}
        >
          {SidebarContent}
        </Sider>
      )}

      <Layout style={{ background: '#f8fafc', marginLeft: isMobile ? 0 : 260, transition: 'all 0.2s', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header style={{
          padding: isMobile ? '0 16px' : '0 32px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
          flexShrink: 0 // Prevent header from shrinking
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ fontSize: '18px', width: 40, height: 40 }}
              />
            )}
            <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 600, color: '#0f172a' }}>
              Overview
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 12px', background: 'rgba(255,255,255,0.5)', borderRadius: 30, border: '1px solid rgba(0,0,0,0.04)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 2px 5px rgba(59, 130, 246, 0.3)' }}>
                <UserOutlined />
              </div>
              {!isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{user?.name || 'User'}</span>
                  <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                    {user?.role === 'super_admin' ? 'Administrator' : 'Sales Exec'}
                  </span>
                </div>
              )}
            </div>

            {!isMobile && <div style={{ width: 1, height: 24, background: '#e2e8f0' }}></div>}

            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                color: '#ef4444',
                borderRadius: 8,
                padding: isMobile ? '4px 8px' : '4px 16px',
                height: 36,
                fontWeight: 600,
                fontSize: 13
              }}
            >
              {isMobile ? '' : 'Logout'}
            </Button>
          </div>
        </Header>
        <Content style={{ margin: isMobile ? '16px' : '32px', flex: 1, overflow: 'initial' }}>
          <div style={{ padding: 0, background: 'transparent' }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
